<template>
  <div
    ref="stEditor"
    class="flex flex-col st-editor grow"
    :class="{ 'page-st-editor': isPageEditor }"
    @mouseleave="handleMouseLeave"
  >
    <TableOfContent
      v-if="currentTableOfContent.length > 0 && editorReady && !hideTableOfContents"
      :key="editorId"
      class="sticky flex flex-col gap-2 right-0 top-2 w-full max-w-[256px] self-end z-[5] bg-background-strongest"
      :items="currentTableOfContent"
      :editor-id="editorId"
      :table-of-content-max-height-offset="tableOfContentMaxHeightOffset"
      @scrollTo="handleScrollTo"
    />
    <StBubbleMenu
      v-if="editor && editorReady"
      :editor="editor"
      plugin-key="tableMenu"
      :tippy-options="tabelMenuTippyOptions"
      :should-show="shouldShowTableMenu"
      class="flex pointer-events-none toolbar-wrap bubble toolbar-skin"
    >
      <StMenu
        ref="tableToolbar"
        class="flex flex-row flex-wrap toolbar-sections-wrap"
        :command-sets="tableCommands"
        :editor="editor"
      />
    </StBubbleMenu>
    <StBubbleMenu
      v-if="editor && editorReady"
      :editor="editor"
      :tippy-options="bubbleMenuTippyOptions"
      :should-show="shouldShowBubbleMenu"
      class="flex pointer-events-none toolbar-wrap bubble toolbar-skin"
    >
      <div
        ref="toolbarWrap"
        class="flex flex-row flex-wrap toolbar-sections-wrap"
      >
        <MenuSection
          v-if="heading"
          ref="section1"
          :editor="editor"
          :commands="headingCommands"
          :style="{ order: bubbleOrder.headingSection }"
        />
        <MenuSection
          ref="section2"
          :editor="editor"
          :commands="textStyleCommands"
          :style="{ order: bubbleOrder.textStyleSection }"
        />
        <DropdownMenuSection
          v-if="enableTextAlignment"
          :command="alignCommand"
          :editor="editor"
          :style="{ order: bubbleOrder.alignTextSection }"
          :dropdown-commands="alignCommands"
        />
        <MenuSection
          ref="section3"
          :editor="editor"
          :style="{ order: bubbleOrder.mediaSection }"
          :commands="bubbleMediaCommands"
        />
        <MenuSection
          v-if="'listsSection' in bubbleOrder"
          ref="section4"
          :editor="editor"
          :commands="listCommands"
          :style="{ order: bubbleOrder.listsSection }"
        />
        <MenuSection
          v-if="blockElements"
          ref="section5"
          :editor="editor"
          :commands="blockElementsCommands"
          :style="{ order: bubbleOrder.blockElementsSection }"
        />
        <MenuSection
          v-if="commentsAllowed"
          :editor="editor"
          :commands="inlineCommentCommands"
          :style="{ order: bubbleOrder.addCommentButton }"
        />
        <MenuSection
          :editor="editor"
          :commands="cardFromSelectionCommands"
          :style="{ order: bubbleOrder.createCardFromSelection }"
        />
        <MenuSection
          v-if="enableAi && aiAssistantEnabled"
          :editor="editor"
          :commands="showAiPopupCommand"
          :options="{
            aiPopupAppendTo: aiAssistantAppendTo,
          }"
          :style="{ order: bubbleOrder.aiSection }"
        />
      </div>
    </StBubbleMenu>
    <editor-content
      class="flex flex-col grow editor-content-wrapper"
      :editor="editor"
      :spellcheck="spellCheck && !typing"
    />
    <EditorAvatars
      v-if="showAvatars"
      :users="editor.storage.collaborationCursor.users"
      :session-avatars-position="sessionAvatarsPosition"
      :status="status"
    />
  </div>
</template>

<script>
import { EditorContent } from '@tiptap/vue-3';
import { BubbleMenuView } from '@tiptap/extension-bubble-menu';
import { StBubbleMenu } from './StBubbleMenu';
import { Doc } from 'yjs';
import { WebsocketProvider } from '@superthread-com/y-websocket';
import { IndexeddbPersistence } from 'y-indexeddb';
import { ResourceType } from '@/types/resources';
import EditorService from './services/EditorService';
import EventBus from '@/utilities/eventBus';
import isFirefox from '@/utilities/isFirefox';
import useAwareness from './composables/useAwareness';
import { userHashColor } from '@/utilities/composables/useUserHashColor.ts';
import getYSyncKey from './helpers/getYSyncKey';
import Paragraph from '@tiptap/extension-paragraph';
import { NodeSelection } from '@tiptap/pm/state';
import MenuSection from '@/components/editor/toolbar/MenuSection';
import StMenu from '@/components/editor/toolbar/Menu';
import { tableCommands } from '@/components/editor/toolbar/table.ts';
import { isInTable } from '@tiptap/pm/tables';
import { posToDOMRect } from '@tiptap/core';
import {
  isFirstRowSelected,
  getVisiblePortionOfElement,
  FLOATING_MENU_HEIGHT,
  ARBITRARY_MENU_OFFSET,
} from '@/components/editor/plugins/table/util';
import { generateEditorId } from '@/utilities/generateEditorId';
import TableOfContent from '@/components/editor/tableOfContents/TableOfContent';
import {
  listCommands,
  headingCommands,
  textStyleCommands,
  bubbleMediaCommands,
  blockElementsCommands,
  inlineCommentCommands,
  cardFromSelectionCommands,
  showAiPopupCommand,
  alignCommands,
  alignCommand,
} from '@/components/editor/toolbar/selection';
import DropdownMenuSection from '@/components/editor/toolbar/sections/DropdownMenuSection';
import useGenerateEditorClass from '@/components/editor/composables/useGenerateEditorClass';
import constants from '@/utilities/constants';
import EditorAvatars from '@/components/editor/EditorAvatars';

const BYTES_LIMIT = constants.pageBytesLimit;
const PERCENTAGE_MULTIPLIER = 100 / BYTES_LIMIT;
const RECALCULATE_SIZE_DEBOUNCE_DELAY = 1000; // Debounce delay in milliseconds
const ydoc = {};

export default {
  name: 'Editor',

  components: {
    EditorContent,
    StBubbleMenu,
    MenuSection,
    TableOfContent,
    StMenu,
    DropdownMenuSection,
    EditorAvatars,
  },

  props: {
    resourceType: {
      type: String,
      required: false,
      default: '',
    },

    resourceId: {
      type: String,
      required: false,
      default: '',
    },

    parentResourceType: {
      type: String,
      required: false,
      default: '',
    },

    parentResourceId: {
      type: String,
      required: false,
      default: '',
    },

    content: {
      type: String,
      required: false,
      default: '',
    },

    inputPlaceHolder: {
      type: String,
      required: false,
      default: '',
    },

    watchContent: {
      type: Boolean,
      required: false,
      default: true,
    },

    commentsAllowed: {
      type: Boolean,
      required: false,
      default: false,
    },

    readOnly: {
      type: Boolean,
      required: false,
      default: false,
    },

    bottomGutter: {
      type: Boolean,
      required: false,
      default: false,
    },

    draggable: {
      type: Boolean,
      required: false,
      default: false,
    },

    heading: {
      type: Boolean,
      required: false,
      default: true,
    },

    bubbleOrder: {
      type: Object,
      required: false,
      default: () => ({
        headingSection: 0,
        mediaSection: 1,
        textStyleSection: 2,
        listsSection: 3,
        blockElementsSection: 4,
        addCommentButton: 5,
        createCardFromSelection: 6,
      }),
    },

    autofocus: {
      type: Boolean,
      required: false,
      default: false,
    },

    bubbleMenuParent: {
      type: HTMLElement,
      required: false,
      default: null,
    },

    allowImages: {
      type: Boolean,
      required: false,
      default: true,
    },

    submitOnEnter: {
      type: Boolean,
      required: false,
      default: false,
    },

    lists: {
      type: Boolean,
      required: false,
      default: true,
    },

    handleArrowLeft: {
      type: Boolean,
      required: false,
      default: false,
    },

    horizontalRule: {
      type: Boolean,
      required: false,
      default: true,
    },

    blockElements: {
      type: Boolean,
      required: false,
      default: true,
    },
    collaboration: {
      type: Object,
      required: false,
      default: () => {},
    },

    collaborationEnabled: {
      type: Boolean,
      required: false,
      default: false,
    },

    spellCheck: {
      type: Boolean,
      required: false,
      default: true,
    },

    showSessionAvatars: {
      type: Boolean,
      required: false,
      default: true,
    },

    sessionAvatarsPosition: {
      type: Object,
      required: false,
      default: () => ({}),
    },

    allowImagesUpload: {
      type: Boolean,
      required: false,
      default: true,
    },

    focusedStatePlaceholder: {
      type: String,
      required: false,
      default: '',
    },

    tableOfContent: {
      type: Boolean,
      required: false,
      default: false,
    },

    tableOfContentMaxHeightOffset: {
      type: Number,
      required: false,
      default: 0,
    },

    enableAi: {
      type: Boolean,
      required: false,
      default: false,
    },

    preventInsertFiles: {
      type: Boolean,
      required: false,
      default: false,
    },

    characterLimit: {
      type: Number,
      required: false,
      default: undefined,
    },

    highlightAllowed: {
      type: Boolean,
      required: false,
      default: false,
    },

    aiAssistantAppendTo: {
      type: String,
      required: false,
      default: '',
    },

    enableTextAlignment: {
      type: Boolean,
      required: false,
      default: false,
    },

    enableContentMenu: {
      type: Boolean,
      required: false,
      default: false,
    },

    enableBoardEmbed: {
      type: Boolean,
      required: false,
      default: false,
    },

    enablePlaceholder: {
      type: Boolean,
      required: false,
      default: true,
    },

    hideTableOfContents: {
      type: Boolean,
      required: false,
      default: false,
    },

    initialDisconnect: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  emits: [
    'collabConnected',
    'enter',
    'ctrlEnter',
    'esc',
    'backspaceAtStart',
    'input',
    'editorReady',
    'blur',
    'focus',
    constants.editor.arrowUpInFirstRow,
    constants.editor.arrowLeftAtDocStart,
    'scrollToAnchor',
    'scrollTo',
    'removePageComment',
  ],

  setup() {
    const { init, destroy, enableFlagInput, enableFlagUpdates, awareness } = useAwareness();
    const { generateEditorClass } = useGenerateEditorClass();

    return {
      initAwareness: init,
      destroyAwareness: destroy,
      enableFlagInput,
      enableFlagUpdates,
      awareness,
      generateEditorClass,
    };
  },

  data() {
    return {
      editor: null,
      editing: false,
      providerWS: undefined,
      // status: 'disconnected' | 'connecting' | 'connected'
      status: 'disconnected',
      resourceWatch: null,
      contentWatcher: null,
      bubbleMenuTippyOptions: {
        maxWidth: 'none',
        appendTo: this.bubbleMenuParent ?? this.$refs.stEditor,
        role: 'poper',
        getReferenceClientRect: () => {
          const rect = posToDOMRect(
            this.editor.view,
            this.editor.view.state.selection.from,
            this.editor.view.state.selection.to
          );
          /**
           * If the first row is selected, nudge the menu down.
           */
          if (isFirstRowSelected(this.editor.state)) {
            const difference = Math.abs(rect.top - rect.bottom);
            rect.top += difference + FLOATING_MENU_HEIGHT + ARBITRARY_MENU_OFFSET;
            rect.bottom += difference + FLOATING_MENU_HEIGHT + ARBITRARY_MENU_OFFSET;
          }
          return rect;
        },
        onClickOutside: (instance) => {
          instance.hide();
        },
      },
      tabelMenuTippyOptions: {
        maxWidth: 'none',
        appendTo: this.bubbleMenuParent ?? this.$refs.stEditor,
        placement: 'top-start',
        offset: [0, 10],
        zIndex: 11,
        role: 'poper',
        getReferenceClientRect: () => {
          const { selection } = this.editor.state;

          let tablePos = null;

          if (selection instanceof NodeSelection && selection.node.type.name === 'table') {
            tablePos = selection.$from.pos;
          } else {
            const $head = selection.$head;
            for (let d = $head.depth; d > 0; d--) {
              if ($head.node(d).type.name === 'table') {
                tablePos = $head.before(d);
                break;
              }
            }
          }

          /**
           * This case should not occur normally. If tablePos is null, it
           * means that the cursor is outside a table, meaning that the
           * "should show" variable should be false, and this method shouldn't
           * be called. However, considering the collaborative aspect, this
           * may happen.
           *
           * Returning anything but a Rect from this function results in an exception.
           */
          if (tablePos === null) {
            return this.bubbleMenuTippyOptions.getReferenceClientRect();
          }

          const fullWidthInCardOrPage =
            this.editor.view.dom.getAttribute('data-table-full') &&
            (this.resourceType === ResourceType.Card ||
              (this.resourceType === ResourceType.Page && this.resourceId !== 'pageTemplate'));
          let tableNode = this.editor.view.nodeDOM(tablePos)?.querySelector('table');

          if (!fullWidthInCardOrPage) {
            tableNode = tableNode?.parentElement;
          }

          /**
           * Same as above, we cannot return non-rect values from this function.
           */
          if (!tableNode || !(tableNode instanceof HTMLElement)) {
            return this.bubbleMenuTippyOptions.getReferenceClientRect();
          }

          return getVisiblePortionOfElement(tableNode);
        },
      },
      originalShouldShow: null,
      editorReady: false,
      localProviderSynced: false,
      tableCommands,
      listCommands,
      headingCommands,
      textStyleCommands,
      bubbleMediaCommands,
      blockElementsCommands,
      inlineCommentCommands,
      cardFromSelectionCommands,
      showAiPopupCommand,
      // actiall align commands,
      alignCommands,
      // used to add align dropdown button inside of bubble menu
      alignCommand,
      // used to prevent emitting input event
      emitHtml: true,
      typingTimeout: null,
      typing: false,
      editorDisplayOffTimeout: null,
      initialDisconnectTimeout: null,
      editorClasses: {
        'st-editor-content': true,
        [this.$constants.editor.bottomGutterClass]: this.bottomGutter,
      },
      currentEditorUsagePercentage: null, // Editor content size percentage
      recalculateUsageTimeout: null, // For debouncing size recalculation
      idleTimeoutReached: false,
      initialSyncOccurred: false,
      connectingToCollaboration: false,
    };
  },

  computed: {
    defaultPlaceholder() {
      return this.isMobile
        ? this.translate('global.placeholder.focusedStatePlaceholderMobile')
        : this.translate('global.placeholder.focusedStatePlaceholder');
    },
    focusedPlaceholderText() {
      return this.focusedStatePlaceholder || this.defaultPlaceholder;
    },
    inputPlaceHolderText() {
      return this.inputPlaceHolder || this.defaultPlaceholder;
    },
    editorId() {
      return generateEditorId(this.resourceType, this.resourceId, this.team?.id);
    },
    user() {
      return this.$store.getters.getUser;
    },
    team() {
      return this.$store.getters.getTeam;
    },
    synced() {
      return this.providerWS && this.providerWS.synced;
    },
    collaborationOptions() {
      return {
        document: ydoc[this.editorId],
        awareness: this.awareness,
        user: {
          id: this.user.id,
          name: this.user.display_name,
          color: this.user.color,
          yjsId: ydoc[this.editorId]?.clientID ?? 0,
        },
      };
    },
    customSessionAvatarsPosition() {
      return Object.keys(this.sessionAvatarsPosition).length > 0;
    },
    localProviderSyncedAndEditorReady() {
      return {
        localProviderSynced: this.localProviderSynced,
        editorReady: this.editorReady,
      };
    },

    currentTableOfContent() {
      if (!this.tableOfContent) return [];
      return this?.editor?.storage?.tableOfContent?.content || [];
    },

    aiAssistantEnabled() {
      return this.$store.getters.getUserFeatureEnabled(this.$constants.featureFlagIds.aiAssistant);
    },

    preventInsertFilesUiNotificationLabel() {
      return this.resourceId === ResourceType.CardTemplate ||
        this.resourceId === ResourceType.PageTemplate
        ? this.translate('uploadFilesForTemplatesIsDisabled')
        : this.translate('uploadFilesIsDisabled');
    },

    isPageEditor() {
      return (
        this.resourceType === ResourceType.Page &&
        this.editorId.includes('editor-page') &&
        !['pageTemplate', 'quick'].includes(this.resourceId)
      );
    },
    showAvatars() {
      return (
        this.editor?.storage?.collaborationCursor &&
        this.showSessionAvatars &&
        !this.idleTimeoutReached &&
        this.collaboration?.token !== this.$constants.newPage
      );
    },
    onlineStatus() {
      return this.$store.getters.getOnline;
    },
  },

  watch: {
    readOnly(newValue) {
      if (newValue) {
        this.editor.setEditable(false, false);
        this.editor.commands.hideDragHandle();
      } else {
        this.editor.setEditable(true, false);
      }
    },

    collaboration(newValue, oldValue) {
      // when there's a new value passed to component, init collaboration
      if (!oldValue?.token && newValue?.token) {
        this.destroyEditor();
        this.setEditor();
        return;
      }

      if (newValue?.token && oldValue?.token && newValue?.token !== oldValue?.token) {
        if (oldValue.token === this.$constants.newPage) {
          this.intLocalProvider();
          this.initCollaboration();
          return;
        }
        this.reconnectCollaboration();
        return;
      }
    },

    onlineStatus(newValue, oldValue) {
      /**
       * If going from offline to online, while collab is enabled but not connected,
       * re-initiate collaboration to gracefully reconnect
       */
      if (newValue && !oldValue && this.collaborationEnabled && this.status !== 'connected')
        this.initCollaboration();
    },

    status: {
      handler() {
        if (this.initialSyncOccurred) {
          // Disconnect from the provider initially to prevent having unnecessary open connections
          this.initialDisconnectTimeout = setTimeout(() => {
            this.providerWS.disconnect();
          }, 100);
        }
      },
      once: true,
    },
  },

  created() {
    this.setEditor();
    this.storeOriginalShouldShowBubbleMenu();
  },

  mounted() {
    if (this.watchContent) {
      this.setContentWatcher();
    }
    if (this.commentsAllowed) {
      EventBus.$on('removePageComment', this.removePageComment);
      EventBus.$on('resolveComment', this.resolveComment);
      EventBus.$on('unresolveComment', this.unresolveComment);
    }
    if (typeof this.characterLimit !== 'undefined') {
      EventBus.$on('editorCharacterLimitReached', this.onCharacterLimitReached);
    }
    EventBus.$on(this.$constants.editor.arrowUpInFirstRow, this.arrowUpInFirstRow);
    EventBus.$on(this.$constants.editor.arrowLeftAtDocStart, this.arrowLeftAtDocStart);
    EventBus.$on('scrollToAnchor', this.scrollToAchor);
    EventBus.$on('preventInsertFiles', this.onPreventInsertFiles);
    EventBus.$on('fileInsertError', this.onFileInsertError);
    EventBus.$on('resetEditor', this.resetEditor);
  },

  beforeUnmount() {
    this.destroyEditor();
    if (this.commentsAllowed) {
      EventBus.$off('removePageComment', this.removePageComment);
      EventBus.$off('resolveComment', this.resolveComment);
      EventBus.$off('unresolveComment', this.unresolveComment);
    }
    EventBus.$off(this.$constants.editor.arrowUpInFirstRow, this.arrowUpInFirstRow);
    EventBus.$off(this.$constants.editor.arrowLeftAtDocStart, this.arrowLeftAtDocStart);
    EventBus.$off('scrollToAnchor', this.scrollToAchor);
    EventBus.$off('preventInsertFiles', this.onPreventInsertFiles);
    EventBus.$off('editorCharacterLimitReached', this.onCharacterLimitReached);
    EventBus.$off('fileInsertError', this.onFileInsertError);
    EventBus.$off('resetEditor', this.resetEditor);
  },

  methods: {
    getCollaborationOptions() {
      return {
        document: ydoc[this.editorId],
        awareness: this.awareness,
        user: {
          id: this.user.id,
          name: this.user.display_name,
          color: this.user.color ?? userHashColor(this.user.id),
          yjsId: ydoc[this.editorId]?.clientID ?? 0,
        },
      };
    },

    intLocalProvider() {
      if (this.providerLocal) {
        this.providerLocal.destroy();
      }
      if (!this.editorId.includes?.(this.$constants.newPage)) {
        const newPageId = Object.keys(ydoc).find((key) => key.includes(this.$constants.newPage));
        if (newPageId) {
          ydoc[this.editorId] = ydoc[newPageId];
          delete ydoc[newPageId];
        }

        this.providerLocal = new IndexeddbPersistence(this.editorId, ydoc[this.editorId]);
        this.$store.dispatch('addPageKeyToPersistedKeys', this.editorId);

        this.syncInitialContent();
      }
    },

    initCollaboration() {
      if (!this.collaboration.token) return;
      if (this.collaboration.token !== this.$constants.newPage) {
        this.emitHtml = false;
      }
      if (this.providerWS) {
        // when there's a new collaboration token, destroy the previous provider
        this.providerWS.destroy();
      }

      this.intLocalProvider();

      this.providerWS = new WebsocketProvider(
        this.$store.getters.getRoutes.editorSocket,
        this.editorId,
        ydoc[this.editorId],
        {
          connect: this.editable,
          awareness: this.awareness,
          params: {
            token: this.collaboration.token,
          },
          disableBc: true,
        }
      );

      this.providerWS.on('document-size', (size) => {
        this.debouncedRecalculateEditorSize(size);
      });

      this.providerWS.on('connection-error', () => {
        this.providerWS.connect();
      });
      this.providerWS.on('connection-close', (event) => {
        this.providerWS.disconnect();

        if (event.code === 3008) {
          this.idleTimeoutReached = true;
          this.blurInput();
        } else if (event.code === 1008) {
          // we need to refresh our token
          switch (this.resourceType) {
            case ResourceType.Page:
              this.$store.dispatch('refreshPageCollaboration', {
                id: this.resourceId,
              });
              break;
            case ResourceType.Card:
              this.$store.dispatch('refreshCardCollaboration', {
                id: this.resourceId,
              });
              break;
          }
        }
      });
      this.providerWS.on('status', ({ status }) => {
        this.status = status;
        if (status === 'connected') {
          this.connectingToCollaboration = false;
          this.$emit('collabConnected');

          // used when creating page from template
          this.editorDisplayOffTimeout = setTimeout(() => {
            this.$store.dispatch('setPageEditorDisplay', {});
          }, 100);
        }
      });

      this.enableFlagUpdates(this.providerWS);
    },

    reconnectCollaboration() {
      if (!this.collaboration?.token) return;
      if (!this.providerWS) {
        this.initCollaboration();
        return;
      }
      this.providerWS.disconnect();
      this.providerWS.roomname = this.editorId;
      this.providerWS.params.token = this.collaboration.token;
      this.providerWS.updateUrl();
      this.providerWS.connect();
    },

    destroyCollaboration() {
      ydoc[this.editorId]?.destroy();
      this.providerWS?.destroy();
      this.providerLocal?.destroy();
      this.destroyAwareness();
    },

    destroyEditor() {
      if (isFirefox()) {
        window.removeEventListener('beforeunload', this.destroyEditor);
      }

      if (this.collaboration && this.collaborationEnabled) {
        this.destroyCollaboration();
      }
      this.editor?.destroy?.();
      this.$store.dispatch('deleteEditor', this.editorId);
      clearTimeout(this.typingTimeout);
      clearTimeout(this.editorDisplayOffTimeout);
      clearTimeout(this.recalculateUsageTimeout); // Clear debounce timeout
      clearTimeout(this.initialDisconnectTimeout);
      this.idleTimeoutReached = false; // Reset idle timeout flag

      // clear out new page ydoc if it exists
      const newPageId = Object.keys(ydoc).find((key) => key.includes(this.$constants.newPage));
      if (newPageId) {
        ydoc[newPageId]?.destroy();
        delete ydoc[newPageId];
      }
    },

    setEditor(content = this.content) {
      // firefox has an issue with terminating cloudflare WS connections (unclear why or if it's a firefox bug or cloudflare bug)
      // we need to destroy and re-init the editor manually beforeunload to circumvent the issue
      if (isFirefox()) {
        window.addEventListener('beforeunload', this.destroyEditor);
      }
      ydoc[this.editorId] = new Doc();
      this.initAwareness(ydoc[this.editorId]);

      if (this.collaboration && this.collaborationEnabled) {
        this.initCollaboration();
      }

      this.editor = new EditorService(
        this.editorId,
        {
          resourceId: this.resourceId,
          resourceType: this.resourceType,
          parentResourceType: this.parentResourceType,
          parentResourceId: this.parentResourceId,
          commenting: this.commentsAllowed,
          ...(this.enablePlaceholder && {
            placeholder: this.inputPlaceHolderText,
          }),
          draggable: this.draggable,
          heading: this.heading,
          allowImages: this.allowImages,
          allowImagesUpload: this.allowImagesUpload,
          preventInsertFiles: this.preventInsertFiles,
          lists: this.lists,
          handleArrowLeft: this.handleArrowLeft,
          horizontalRule: this.horizontalRule,
          blockElements: this.blockElements,
          ...(this.enablePlaceholder && {
            focusedStatePlaceholder: this.focusedPlaceholderText,
          }),
          ...(this.collaborationEnabled && {
            collaboration: this.getCollaborationOptions(),
          }),
          tableOfContent: this.tableOfContent,
          editorDomRef: this.$refs.stEditor || null,
          characterLimit: this.characterLimit,
          highlight: this.highlightAllowed,
          textAlignment: this.enableTextAlignment,
          enableContentMenu: this.enableContentMenu,
          enableBoardEmbed: this.enableBoardEmbed,
          storeDispatch: this.$store.dispatch,
        },
        {
          ...((this.readOnly || !this.collaborationEnabled) && { content }),
          editable: !this.readOnly,
          autofocus: this.autofocus,
          editorProps: {
            attributes: {
              id: this.editorId,
              class: this.generateEditorClass(),
            },

            handleKeyDown: (view, event) => {
              this.checkAndSetTyping();
              if (this.editor.storage.mention.mentionDropdownActive) return false;

              if (this.submitOnEnter && !event.ctrlKey && !event.metaKey && event.key === 'Enter') {
                this.$emit('enter');
                return this.submitOnEnter;
              }
              if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
                this.$emit('ctrlEnter');
                return true;
              }
              // cmd + backslash
              if ((event.ctrlKey || event.metaKey) && event.key === '\\') {
                this.editor.can().hideBlockMenu();
                EventBus.$emit('cmdBackslash', event);
                return false;
              }
              if (event.key === 'Escape') {
                const { from, to } = view.state.selection;
                if (from !== to) {
                  // set selection to last position and close menu
                  this.editor.commands.setTextSelection(to);
                  event.stopPropagation();
                } else {
                  this.$emit('esc', event);
                }
                return true;
              }
              if (event.key === 'Backspace') {
                const { $from, $to } = view.state.selection;
                const { firstChild } = view.state.doc;
                const { name } = firstChild.type;
                if (
                  $from.parentOffset === 0 &&
                  $to.parentOffset === 0 &&
                  $from.index(0) === 0 &&
                  $from.pos === $to.pos
                ) {
                  // The cursor is at the beginning of the current node
                  if (name === Paragraph.name) {
                    this.$emit('backspaceAtStart');
                    return true;
                  }
                }
              }
              return false;
            },
          },
          parseOptions: {
            preserveWhitespace: true,
          },
          onUpdate: ({ transaction }) => {
            // if transaction contains ysync object it comes from collaboration
            if (!getYSyncKey(transaction)) {
              this.editor.commands.hideBlockMenu();
            }
            // don't emit update if meta.addToHistory is false
            if (transaction.meta?.addToHistory == false && !transaction.meta?.imageInsert) return;

            // emit input event when content has changed
            if (this.emitHtml) {
              this.$emit('input', this.editor.getHTML());
            }

            // initiate collaboration if not connected and not already connecting
            if (
              this.collaborationEnabled &&
              !this.connectingToCollaboration &&
              this.status !== 'connected'
            ) {
              this.connectingToCollaboration = true;
              this.initCollaboration();
            }
          },
          onCreate: ({ editor }) => {
            // The editor is ready.
            this.editorReady = true;
            this.$emit('editorReady', editor);
            this.enableFlagInput(editor);

            // when user is guest, we need to hide the real name of some users in the awareness
            if (this.isGuestRole) {
              const hiddenMemberLabel = this.translate('hiddenMember');
              const replaceAwarenessUserLabel = (id) => {
                const awarenessState = this.awareness?.states?.get?.(id);

                const member = this.$store.getters.getMemberById(awarenessState?.user?.id);
                const memberNotFound = Object.keys(member).length === 0;
                // if member is not found in the teamMembers returned from BE,
                // it means it's a hidden member
                if (memberNotFound && awarenessState.user) {
                  awarenessState.user.name = hiddenMemberLabel;
                  this.awareness?.states?.set(id, awarenessState);
                }
              };
              this.awareness.on('update', ({ added, updated }) => {
                added.forEach((id) => replaceAwarenessUserLabel(id));
                updated.forEach((id) => replaceAwarenessUserLabel(id));
              });
            }
          },
          onBlur: ({ event: _event }) => {
            // if click is on toolbar
            if (this.editor.storage.mention.mentionDropdownActive) return;
            if (_event?.relatedTarget?.matches('.toolbar-wrap *, .toolbar-wrap, .tippy-box'))
              return;
            this.$emit('blur', _event);
          },

          onFocus: () => {
            this.$store.dispatch('setCurrentEditorResourceType', this.resourceType);
            this.$store.dispatch('setCurrentEditorId', this.editor.id);
            this.$emit('focus');

            if (this.initialSyncOccurred || this.idleTimeoutReached) this.initCollaboration();
            if (this.initialSyncOccurred) this.initialSyncOccurred = false;
            if (this.idleTimeoutReached) this.idleTimeoutReached = false;
          },
        }
      );

      this.$store.dispatch('setEditor', this.editor);
    },

    focusEditor(position = 'end') {
      this.editor.commands?.focus(position);
    },

    setContentWatcher() {
      this.contentWatcher = this.$watch('content', (value) => {
        if (value) {
          this.destroyEditor();
          this.setEditor(value);
          this.contentWatcher();
        }
      });
    },

    removeHiglihtSelection() {
      this.editor.commands.removePreCommentHighlightSelection();
    },

    addInlineComment(commentId) {
      this.editor.commands.setComment(commentId);
    },
    arrowUpInFirstRow({ editorId, rect }) {
      if (editorId !== this.editorId) return;
      this.$emit(this.$constants.editor.arrowUpInFirstRow, { editorId, rect });
    },
    arrowLeftAtDocStart({ editorId }) {
      if (editorId !== this.editorId) return;
      this.$emit(this.$constants.editor.arrowLeftAtDocStart, { editorId });
    },

    removePageComment(commentToDeleteId) {
      if (this.$store.getters.activeComment === commentToDeleteId) {
        this.$emit('removePageComment');
      }
      this.editor.commands.removeComment(commentToDeleteId);
    },

    resolveComment(commentId) {
      this.editor.commands.resolveComment(commentId);
    },

    unresolveComment(commentId) {
      this.editor.commands.unresolveComment(commentId);
    },

    storeOriginalShouldShowBubbleMenu() {
      const element = document?.createElement?.('div') || null;
      const BubbleMenuViewInstance = new BubbleMenuView({
        editor: this.editor,
        element,
        view: this.editor.view,
        tippyOptions: {
          onShow: () => false,
        },
      });
      this.originalShouldShow = BubbleMenuViewInstance.shouldShow;
    },

    shouldShowBubbleMenu({ view, state, from, to }) {
      // no need to display bubble menu if selection is nodeSelection
      const { node } = view.state.selection;
      return this.originalShouldShow?.({ view, state, from, to }) && !node;
    },

    shouldShowTableMenu() {
      return isInTable(this.editor.view.state) && this.editor.view.hasFocus();
    },

    handleMouseLeave() {
      this.editor.commands.hideBlockMenu?.();
    },

    clearEditor() {
      this.editor.commands.clearContent();
    },

    selectAll() {
      this.editor.commands.selectAll();
    },

    blurInput() {
      this.editor.commands.blur();
    },

    setContent(content) {
      this.editor.commands.setContent(content);
    },

    isEmpty() {
      return this.editor.isEmpty;
    },

    setLastSelection() {
      const { $from, $to } = this.editor?.view?.state?.selection;
      this.editor.commands.setTextSelection($from.pos, $to.pos);
      this.editor.commands.focus();
    },

    scrollToAchor({ editorId, url }) {
      if (editorId !== this.editorId) return;
      this.$emit('scrollToAnchor', url);
    },

    handleScrollTo(id) {
      this.$emit('scrollTo', id, 0, 120);
    },

    onPreventInsertFiles(editorId) {
      if (editorId !== this.editorId) return;
      this.$store.dispatch('addUiNotification', {
        message: this.preventInsertFilesUiNotificationLabel,
        status: this.$constants.uiNotificationStatuses.warning,
        duration: 4000,
      });
    },
    onFileInsertError(data) {
      if (data.editorId !== this.editorId) return;
      this.$store.dispatch('removeEditorUpload', data.uploadId);
      this.$store.dispatch('addUiNotification', {
        message: this.translate('editor.genericFileError'),
        status: this.$constants.uiNotificationStatuses.error,
        duration: 4000,
      });
    },
    onCharacterLimitReached(editorId) {
      if (editorId !== this.editorId) return;
      this.$store.dispatch('addUiNotification', {
        message: this.translate('editor.characterLimitReachedTryFile'),
        status: this.$constants.uiNotificationStatuses.warning,
        duration: 4000,
        id: `${this.editorId}-climit`,
      });
    },

    checkAndSetTyping() {
      if (!this.typing) {
        this.typing = true;
      }
      if (this.typingTimeout) clearTimeout(this.typingTimeout);
      this.typingTimeout = setTimeout(() => {
        this.typing = false;
      }, 200);
    },

    async resetEditor(resourceId) {
      await this.$nextTick();

      if (resourceId !== this.resourceId) return;

      this.destroyEditor();
      this.setEditor();
      this.focusEditor('end');
    },

    debouncedRecalculateEditorSize(size) {
      if (!this.editor || !this.editorId) return; // Guard against missing editor/editorId

      clearTimeout(this.recalculateUsageTimeout);
      this.recalculateUsageTimeout = setTimeout(async () => {
        // Ensure editor instance still exists after timeout
        if (this.editor && this.isPageEditor) {
          const percentage = Math.min(size * PERCENTAGE_MULTIPLIER, 100);
          this.currentEditorUsagePercentage = Math.round(percentage * 100) / 100;
          this.editor.updateEditorProgressPercentage(this.currentEditorUsagePercentage);
        }
        clearTimeout(this.recalculateUsageTimeout);
        this.recalculateUsageTimeout = null;
      }, RECALCULATE_SIZE_DEBOUNCE_DELAY);
    },

    syncInitialContent() {
      if (!this.initialDisconnect) return;

      this.providerLocal.on('synced', () => {
        // On first content sync, mark that sync has occurred so we can disconnect
        if (this.collaborationEnabled && !this.initialSyncOccurred) this.initialSyncOccurred = true;
      });
    },
  },
};
</script>

<style lang="scss">
@import './styles/ycursors.scss';

.st-drop-cursor-block {
  max-width: calc(
    100% - 2 * var(--page-elements-side-padding) - 2 * $pageElementsSidePadding
  ) !important;

  left: calc(var(--page-elements-side-padding) + $pageElementsSidePadding) !important;
  @apply opacity-40 rounded;
}

.st-editor {
  line-height: 23px;
  .st-editor-content {
    @apply text-text-default select-text;

    &.resize-cursor {
      cursor: col-resize !important;
    }

    &.force-placeholder-off {
      .is-empty::before {
        content: '' !important;
      }
    }

    > *:first-child {
      &:not(.embedded-board) {
        margin-top: 0 !important;
      }
    }

    @apply flex flex-col grow;
    @import '@/assets/scss/typographyContentNewEditor';

    .ProseMirror-gapcursor {
      position: relative;
      display: flex;
      &:after {
        border-top: 1px solid transparent;
      }
    }
    &:not(.ProseMirror-focused) {
      p[data-placeholder]:not(:first-child) {
        &::before {
          display: none !important;
        }
      }
    }
    &.ProseMirror-focused {
      .ProseMirror-gapcursor {
        &:after {
          border-top: 1px solid var(--textDefault);
        }
      }
    }

    p {
      &::before {
        display: none;
      }
    }
  }

  &__footer {
    @apply text-text-default;
    position: relative;
    bottom: 0;
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    white-space: nowrap;
    border-top: 2px solid var(--textDefault);
    font-size: 12px;
    font-weight: 600;
    padding: 0.25rem 0.75rem;
  }

  /* Some information about the status */
  &__status {
    display: flex;
    align-items: center;
    border-radius: 5px;

    &::before {
      content: ' ';
      flex: 0 0 auto;
      display: inline-block;
      width: 0.5rem;
      height: 0.5rem;
      border-radius: 50%;
      margin-right: 0.5rem;
    }

    &--connecting::before {
      background: #616161;
    }

    &--connected::before {
      background: #b9f18d;
    }
  }

  &__sync {
    display: flex;
  }

  &__name {
    font: inherit;
    font-size: 12px;
    font-weight: 600;
    border-radius: 0.4rem;
    padding: 0.25rem 0.5rem;
  }

  &__users {
    display: flex;

    & > * {
      margin-right: 6px;
    }

    & > *:last-child {
      margin-right: 0;
    }
  }

  // added to prevent list scroll when scrolling through the page
  &.scrolling {
    .embedded-board {
      pointer-events: none;
    }
  }

  &.page-st-editor {
    @media not print {
      .editor-content-wrapper {
        @apply items-center;
      }
    }
    @import '@/assets/scss/page';
  }
}

.ProseMirror-resizing {
  cursor: col-resize !important;

  a:-webkit-any(:link, :visited, :hover, :active) {
    cursor: col-resize !important;
  }

  a:-webkit-any-link:read-write {
    cursor: col-resize !important;
  }
}

.toolbar-wrap {
  &.bubble {
    .toolbar-sections-wrap {
      .tb-button-group {
        button {
          @apply pointer-events-auto;
        }

        &:not(:last-of-type) {
          @apply border-r
          border-r-dividers-default;
        }

        &:not(:last-of-type):not(:first-of-type) {
          @apply px-2;
        }

        &:first-of-type {
          @apply pl-0
            pr-2;
        }

        &:last-of-type {
          @apply pr-0
            pl-2;
        }

        &:only-child {
          @apply pl-0 pr-0;
        }
      }
    }
  }
}

@media (min-width: 768px) {
  .st-editor {
    .st-editor-content {
      .ProseMirror-gapcursor {
        width: 700px;
        max-width: 100%;
        margin: 0 auto;
      }
    }
  }
}

.mobile {
  .st-editor {
    .st-editor-content {
      padding: 0 $pageSideMobilePadding;
    }
  }

  .description-row {
    .st-editor {
      .st-editor-content {
        @apply p-0;
      }
    }
  }
}
</style>
